import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Form, message, Modal, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Roles } from '../../enums/Roles';
import i18n from '../../i18n';
import type { Employee } from '../../models/Employee';
import type { ExportTimeLogsParams } from '../../stores/KanbanTaskStores';
import type { Project } from '../../stores/ProjectsStore';

/** Company owners and admins, and admins of this project, may export its time logs. */
export function canExportTimeLogs(project: Project | undefined, user: Employee | undefined): boolean {
  if (!user) return false;
  if (user.roles?.some((role) => [Roles.Owner, Roles.Admin].includes(role.id))) return true;
  return (
    project?.members?.some(
      (member) => String(member.id) === String(user.id) && Number(member.role?.id) === Roles.Admin,
    ) ?? false
  );
}

export type ExportTimeLogsModalProps = {
  open: boolean;
  onCancel: () => void;
  exporting: boolean;
  projectId: string;
  userId: string;
  onExport: (params: ExportTimeLogsParams) => Promise<boolean>;
  t: (key: string) => string;
};

const ExportTimeLogsModal: React.FC<ExportTimeLogsModalProps> = ({
  open,
  onCancel,
  exporting,
  projectId,
  userId,
  onExport,
  t,
}) => {
  const [period, setPeriod] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [exportAllLogs, setExportAllLogs] = useState(false);

  const close = () => {
    setPeriod(null);
    setExportAllLogs(false);
    onCancel();
  };

  const submit = async () => {
    const ok = await onExport({
      userId,
      projectId,
      startPeriod: period?.[0]?.startOf('day').toISOString(),
      endPeriod: period?.[1]?.endOf('day').toISOString(),
      exportAllLogs,
      language: (i18n.resolvedLanguage ?? i18n.language ?? 'en').split('-')[0],
    });
    if (!ok) {
      message.error(t('ExportFailed'));
      return;
    }
    close();
  };

  return (
    <Modal
      title={t('ExportTimeLogs')}
      open={open}
      onCancel={close}
      footer={[
        <Button key="cancel" onClick={close} className="hover-effect">
          {t('Cancel')}
        </Button>,
        <Button
          key="export"
          type="primary"
          onClick={() => {
            void submit();
          }}
          className="hover-effect"
        >
          {t('Export')}
        </Button>,
      ]}
    >
      <Spin spinning={exporting}>
        <Form layout="vertical">
          <Form.Item label={t('Period')}>
            <DatePicker.RangePicker
              value={period as any}
              onChange={(vals) => setPeriod(vals as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              allowClear
              format="YYYY-MM-DD"
              className="hover-effect"
            />
          </Form.Item>

          <Form.Item>
            <Checkbox checked={exportAllLogs} onChange={(e) => setExportAllLogs(e.target.checked)}>
              {t('ExportAllProjectLogs')}
              <Tooltip title={t('ExportAllProjectLogsHint')}>
                <Button type="text" icon={<InfoCircleOutlined />} style={{ padding: 0, marginLeft: 4 }} />
              </Tooltip>
            </Checkbox>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ExportTimeLogsModal;
